import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, Print } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#0d47a1';
const PRIMARY_MID = '#1565c0';
const PRIMARY_LT  = '#e3f2fd';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const CashFlowStatement = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);

    useEffect(() => {
        if (companyId && branchId && token) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true); setError('');
        try {
            const res = await fetchData(
                'MISReportApi',
                `/CashFlowStatement/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch cash flow statement');
        } finally {
            setLoading(false);
        }
    };

    const totals = useMemo(() => ({
        debit:  data.reduce((s, r) => s + (parseFloat(r.Debit)  || 0), 0),
        credit: data.reduce((s, r) => s + (parseFloat(r.Credit) || 0), 0),
    }), [data]);

    // Net Cash Flow = total inflows (credit) minus total outflows (debit),
    // excluding Opening/Closing Balance rows which are structural entries, not cash movements
    const netCashFlow = useMemo(() => {
        const flowRows = data.filter(r => {
            const p = (r.Particular || '').trim().toLowerCase();
            return !p.includes('opening balance') && !p.includes('closing balance');
        });
        const flowCredit = flowRows.reduce((s, r) => s + (parseFloat(r.Credit) || 0), 0);
        const flowDebit  = flowRows.reduce((s, r) => s + (parseFloat(r.Debit)  || 0), 0);
        return flowCredit - flowDebit;
    }, [data]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'CashFlowStatement', 0);
        if (!data.length) return;
        const headers = ['#', 'Particular', 'Debit (₹)', 'Credit (₹)'];
        const rows = data.map((r, i) => [
            i + 1,
            r.Particular || '',
            parseFloat(r.Debit)  || 0,
            parseFloat(r.Credit) || 0,
        ]);

        // Add totals row
        rows.push(['', 'Totals', totals.debit, totals.credit]);
        // Add net cash flow row
        rows.push(['', 'Net Cash Flow', '', netCashFlow]);

        await exportStyledExcel({
            reportTitle: 'CASH FLOW STATEMENT',
            sheetName: 'Cash Flow',
            fileName: `CashFlowStatement_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            columnWidths: [4,40,18,18],
            summaryStartIndex: data.length,
        });
    };

    const thBase = {
        padding: '7px 12px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap',
        border: `1px solid ${PRIMARY_MID}`, color: '#fff', background: PRIMARY_MID,
    };
    const tdBase = { padding: '6px 12px', fontSize: 12, border: '1px solid #e2e8f0', color: '#374151' };
    const tdR    = { ...tdBase, textAlign: 'right' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading cash flow statement..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Cash Flow Statement
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>MIS Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!data.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Print" arrow>
                        <IconButton onClick={() => window.print()} disabled={!data.length} size="small" sx={iconBtn}>
                            <Print sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary cards */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '8px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Debit',    value: fmt(totals.debit),   color: '#c62828' },
                            { label: 'Total Credit',   value: fmt(totals.credit),  color: '#2e7d32' },
                            { label: 'Net Cash Flow',  value: fmt(netCashFlow),    color: netCashFlow >= 0 ? '#2e7d32' : '#c62828' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '5px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 13, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                        <div style={{ alignSelf: 'center', marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>
                            {fromDate} to {toDate} &nbsp;|&nbsp; {data.length} entries
                        </div>
                    </div>
                )}

                {/* Table */}
                <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 180px)', scrollbarWidth: 'thin', scrollbarColor: `${PRIMARY} #f0f0f0` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)` }}>
                                <th style={{ ...thBase, textAlign: 'center', width: 40 }}>#</th>
                                <th style={{ ...thBase }}>Particular</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 150 }}>Debit (₹)</th>
                                <th style={{ ...thBase, textAlign: 'right', minWidth: 150 }}>Credit (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : data.map((r, i) => {
                                const debit  = parseFloat(r.Debit)  || 0;
                                const credit = parseFloat(r.Credit) || 0;
                                const bg = i % 2 === 0 ? '#fff' : PRIMARY_LT;
                                return (
                                    <tr key={i} style={{ background: bg }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, fontWeight: 500, color: '#1e293b' }}>{r.Particular || '-'}</td>
                                        <td style={{ ...tdR, color: debit  ? '#c62828' : '#cbd5e1', fontWeight: debit  ? 600 : 400 }}>
                                            {debit  ? fmt(debit)  : '-'}
                                        </td>
                                        <td style={{ ...tdR, color: credit ? '#2e7d32' : '#cbd5e1', fontWeight: credit ? 600 : 400 }}>
                                            {credit ? fmt(credit) : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {data.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={2} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#c62828' }}>{fmt(totals.debit)}</td>
                                    <td style={{ ...tdR, fontWeight: 700, color: '#2e7d32' }}>{fmt(totals.credit)}</td>
                                </tr>
                                <tr style={{ background: netCashFlow >= 0 ? '#e8f5e9' : '#fce4ec', position: 'sticky', bottom: 0 }}>
                                    <td colSpan={3} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Net Cash Flow</td>
                                    <td style={{ ...tdR, fontWeight: 700, fontSize: 13, color: netCashFlow >= 0 ? '#2e7d32' : '#c62828' }}>
                                        {fmt(netCashFlow)}
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CashFlowStatement;
